import axios from '@/libs/axios'

const BASE_URL = 'https://notification.manyconvert.com'
const APP_URL = 'https://app.manyconvert.com'

/**
 * Métodos de pagamento disponíveis para notificações
 */
export const METODOS_PAGAMENTO = {
  BOLETO: { id: 2, nome: 'Boleto' },
  PIX: { id: 6, nome: 'PIX' },
  CARRINHO_ABANDONADO: { id: 1, nome: 'Carrinho Abandonado' },
  GERAL: { id: 19, nome: 'Geral' },
}

/**
 * Status disponíveis para notificações
 * IDs conforme banco de dados do sistema
 */
export const STATUS_NOTIFICACOES = [
  {
    id: 1,
    nome: 'Aprovado',
    descricao: 'Enviado quando o pedido é aprovado',
    icon: 'CheckCircleIcon',
    color: 'success',
  },
  {
    id: 2,
    nome: 'Aguardando Pagamento',
    descricao: 'Enviado quando o pedido está aguardando pagamento',
    icon: 'ClockIcon',
    color: 'info',
  },
  {
    id: 3,
    nome: 'Cancelado',
    descricao: 'Enviado quando o pedido é cancelado',
    icon: 'XCircleIcon',
    color: 'danger',
  },
  {
    id: 4,
    nome: 'Transporte',
    descricao: 'Enviado quando o pedido está em transporte',
    icon: 'TruckIcon',
    color: 'primary',
  },
  {
    id: 5,
    nome: 'Entregue',
    descricao: 'Enviado quando o pedido é entregue',
    icon: 'CheckCircleIcon',
    color: 'success',
  },
  {
    id: 6,
    nome: 'Boleto Vencido',
    descricao: 'Enviado quando o boleto vence',
    icon: 'AlertTriangleIcon',
    color: 'danger',
  },
  {
    id: 7,
    nome: 'Aprovado Parcialmente',
    descricao: 'Enviado quando o pedido é parcialmente aprovado',
    icon: 'AlertCircleIcon',
    color: 'warning',
  },
  {
    id: 8,
    nome: 'Carrinho Abandonado',
    descricao: 'Enviado quando o cliente abandona o carrinho',
    icon: 'ShoppingCartIcon',
    color: 'warning',
  },
  {
    id: 9,
    nome: 'Aprovado e Integrado',
    descricao: 'Enviado quando o pedido é aprovado e integrado',
    icon: 'CheckCircleIcon',
    color: 'success',
  },
  {
    id: 10,
    nome: 'Transporte Interno',
    descricao: 'Enviado quando o pedido está em transporte interno',
    icon: 'TruckIcon',
    color: 'primary',
  },
  {
    id: 11,
    nome: 'Cashback',
    descricao: 'Enviado quando o cliente recebe cashback',
    icon: 'DollarSignIcon',
    color: 'success',
  },
]

/**
 * Busca todas as notificações configuradas para uma loja
 * @param {string} storeUuid - UUID da loja
 * @returns {Promise<Array>} Lista de notificações
 */
export async function listarNotificacoes(storeUuid) {
  try {
    const response = await axios.get(`${BASE_URL}/api/templates/${storeUuid}`)

    // A API retorna { sucesso, templates }
    if (response.data && response.data.templates) {
      return response.data.templates
    }

    return []
  } catch (error) {
    console.error('Erro ao listar notificações:', error)
    throw error
  }
}

/**
 * Cria uma nova configuração de notificação
 * @param {Object} notificacao - Dados da notificação com template mapeado
 * @param {string} storeUuid - UUID da loja
 * @returns {Promise<Object>} Notificação criada
 */
export async function criarNotificacao(notificacao, storeUuid) {
  try {
    // Construir payload com os dados completos do template
    const payload = {
      store_uuid: storeUuid,
      status_id: notificacao.status_type, // Mapear status_type para status_id
      payment_id: notificacao.payment_id,
      template_name_meta: notificacao.template_name_meta,
      linguage: notificacao.linguagem || 'pt_BR', // Mapear linguagem para linguage
      body: notificacao.body,
      variaveis: notificacao.variaveis || [],
      botoes: notificacao.botoes || [],
      header: notificacao.header || null,
      delay_minutes: notificacao.delay_minutes || 0,
    }

    // Adicionar dados de mídia se existirem
    if (notificacao.header_media_id) {
      payload.header_media_id = notificacao.header_media_id
    }
    if (notificacao.header_media_url) {
      payload.header_media_url = notificacao.header_media_url
    }
    if (notificacao.header_media_type) {
      payload.header_media_type = notificacao.header_media_type
    }

    console.log('🚀 Criando notificação com payload:', payload)

    const response = await axios.post(`${BASE_URL}/api/templates`, payload)

    return response.data.data || response.data
  } catch (error) {
    console.error('Erro ao criar notificação:', error)
    throw error
  }
}

/**
 * Atualiza uma configuração de notificação (template)
 * @param {number} id - ID do template
 * @param {Object} notificacao - Dados atualizados incluindo o template completo
 * @param {string} storeUuid - UUID da loja
 * @returns {Promise<Object>} Notificação atualizada
 */
export async function atualizarNotificacao(id, notificacao, storeUuid) {
  try {
    // Construir payload com os dados completos do template selecionado
    const payload = {
      store_uuid: storeUuid,
      template_name_meta: notificacao.template_name_meta,
      linguage: notificacao.linguagem || 'pt_BR', // Mapear linguagem para linguage
      body: notificacao.body,
      variaveis: notificacao.variaveis, // Já vem como array do template
      botoes: notificacao.botoes, // Já vem como array do template
      header: notificacao.header, // Já vem como objeto do template
    }

    // Adicionar dados de mídia se existirem
    if (notificacao.header_media_id) {
      payload.header_media_id = notificacao.header_media_id
    }
    if (notificacao.header_media_url) {
      payload.header_media_url = notificacao.header_media_url
    }
    if (notificacao.header_media_type) {
      payload.header_media_type = notificacao.header_media_type
    }

    const response = await axios.put(`${BASE_URL}/api/templates/${id}`, payload)

    return response.data.data || response.data
  } catch (error) {
    console.error('Erro ao atualizar notificação:', error)
    throw error
  }
}

/**
 * Exclui uma configuração de notificação
 * @param {Object} dados - Dados para exclusão
 * @param {string} dados.store_uuid - UUID da loja
 * @param {number} dados.status_id - ID do status
 * @param {number} dados.payment_id - ID do método de pagamento
 * @returns {Promise<boolean>} Sucesso da operação
 */
export async function excluirNotificacao(dados) {
  try {
    await axios.delete(`${BASE_URL}/api/templates`, {
      data: {
        store_uuid: dados.store_uuid,
        status_id: dados.status_id,
        payment_id: dados.payment_id,
      },
    })
    return true
  } catch (error) {
    console.error('Erro ao excluir notificação:', error)
    throw error
  }
}

/**
 * Retorna o payment_id correto baseado no status
 * @param {number} statusId - ID do status
 * @param {number} paymentId - ID do método de pagamento (opcional, usado para Aguardando Pagamento)
 * @returns {number} Payment ID
 */
export function getPaymentIdPorStatus(statusId, paymentId = null) {
  // Status: Carrinho Abandonado
  if (statusId === 8) {
    return 1
  }

  // Status: Aguardando Pagamento - pode ter PIX (6) ou Boleto (2)
  if (statusId === 2) {
    return paymentId || 19 // Se não especificar, usa 19 como padrão
  }

  // Outros status
  return 19
}

/**
 * Ativa ou desativa uma notificação
 * @param {number} id - ID do template
 * @param {boolean} ativo - Status desejado (true = ativo, false = inativo)
 * @param {string} storeUuid - UUID da loja
 * @returns {Promise<Object>} Notificação atualizada
 */
export async function toggleNotificacaoAtiva(id, ativo, storeUuid) {
  try {
    const response = await axios.patch(`${BASE_URL}/api/templates/${id}/toggle`, {
      store_uuid: storeUuid,
      ativo,
    })
    return response.data
  } catch (error) {
    console.error('Erro ao alterar status da notificação:', error)
    throw error
  }
}

/**
 * Informa o back-end se as notificações da loja estão ativadas ou desativadas.
 * @param {boolean} status - true = ativadas, false = desativadas
 * @param {string} uuid - UUID da loja
 */
export async function atualizarStatusNotificacoes(status, uuid) {
  try {
    await axios.put(`${APP_URL}/api/notifications/status`, {
      uuid,
      status,
    })
  } catch (error) {
    console.error('Erro ao atualizar status de notificações no back-end:', error)
  }
}

/**
 * Atualiza o flag isOficial da loja (1 = notificações ativadas, 0 = desativadas).
 * @param {string} uuid - UUID da loja
 * @param {number} isOficial - 1 quando notificações ativadas, 0 quando desativadas
 */
export async function atualizarIsOficialStore(uuid, isOficial) {
  try {
    await axios.patch(`${BASE_URL}/api/stores/${uuid}/isOficial`, {
      isOficial,
    })
  } catch (error) {
    console.error('Erro ao atualizar isOficial da loja:', error)
  }
}

/**
 * Busca templates do WhatsApp disponíveis para uma loja
 * Este endpoint retorna os templates aprovados no WhatsApp Cloud API
 * @param {string} storeUuid - UUID da loja
 * @returns {Promise<Array>} Lista de templates do WhatsApp
 */
export async function buscarTemplatesNotificacao(storeUuid) {
  try {
    const response = await axios.get(`${BASE_URL}/api/cloud/templates/${storeUuid}`)

    // A API retorna os templates do WhatsApp Cloud
    if (response.data && response.data.templates) {
      return response.data.templates
    }

    // Se a resposta for um array direto
    if (Array.isArray(response.data)) {
      return response.data
    }

    return []
  } catch (error) {
    console.error('Erro ao buscar templates do WhatsApp:', error)
    throw error
  }
}

/**
 * Faz upload de mídia (imagem ou vídeo) para o servidor
 * @param {File} file - Arquivo de mídia
 * @param {string} storeUuid - UUID da loja
 * @param {string} mediaType - Tipo de mídia ('image' ou 'video')
 * @returns {Promise<Object>} Objeto com header_media_id, header_media_url e header_media_type
 */
export async function uploadMediaNotificacao(file, storeUuid, mediaType) {
  try {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('store_uuid', storeUuid)
    formData.append('media_type', mediaType)

    const response = await axios.post(`${BASE_URL}/api/templates/upload-media`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })

    // Retornar os dados completos da mídia
    const { data } = response

    return {
      header_media_id: data.header_media_id || data.media_id || data.id,
      header_media_url: data.header_media_url || data.media_url || data.url,
      header_media_type: data.header_media_type || data.media_type || mediaType,
    }
  } catch (error) {
    console.error('Erro ao fazer upload de mídia:', error)
    throw error
  }
}
